'use client'

import type { KeyboardEvent, PointerEvent } from 'react'
import { Loader2, Mic } from 'lucide-react'
import { cn } from '@/lib/utils'

/** Espacio/Intro: empezar y terminar la nota con teclado. */
export const TOGGLE_KEYS = new Set([' ', 'Enter'])

/** Mantener pulsado para grabar (`useHoldToTalk` del compositor). */
export interface MicHandlers {
  onPointerDown: (event: PointerEvent<HTMLButtonElement>) => void
  onPointerMove: (event: PointerEvent<HTMLButtonElement>) => void
  onPointerUp: () => void
  onPointerCancel: () => void
  onKeyDown: (event: KeyboardEvent<HTMLButtonElement>) => void
}

export interface MicButtonProps {
  label: string
  disabled: boolean
  /**
   * Papá Noel está contestando: el botón no graba (`aria-disabled`, no `disabled`,
   * para que el toque llegue) y cada intento lo explica y sacude el icono.
   */
  waiting: { nudge: () => void; count: number } | null
  recording: boolean
  /** Pulsado, esperando a que arranque el micrófono. */
  arming: boolean
  /** Primera vez: un anillo que late alrededor enseña dónde tocar. */
  coaching: boolean
  handlers: MicHandlers
}

/** Mientras espera, ningún gesto graba: cualquier intento solo explica por qué. */
function waitingHandlers(nudge: () => void) {
  return {
    onPointerDown: nudge,
    onKeyDown: (event: KeyboardEvent<HTMLButtonElement>) => {
      if (!TOGGLE_KEYS.has(event.key)) return
      event.preventDefault()
      nudge()
    },
  }
}

// Same 48 px as the text field (h-12): still above the 44 px touch minimum.
const MIC_BUTTON =
  'relative flex size-12 shrink-0 touch-none select-none items-center justify-center rounded-full bg-primary text-primary-foreground shadow-md transition-transform focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring focus-visible:ring-offset-2 focus-visible:ring-offset-background disabled:opacity-50'

function MicIcon({ arming, shakeKey }: { arming: boolean; shakeKey: number | null }) {
  if (arming) return <Loader2 aria-hidden className="size-6 motion-safe:animate-spin" />
  return (
    <span key={shakeKey ?? 'still'} className={cn('flex', shakeKey && 'animate-shake')}>
      <Mic aria-hidden className="size-6" />
    </span>
  )
}

export function MicButton({ label, disabled, waiting, recording, arming, coaching, handlers }: MicButtonProps) {
  return (
    <span className="relative flex shrink-0">
      {coaching && (
        <span
          aria-hidden
          data-testid="mic-coach"
          className="pointer-events-none absolute inset-0 rounded-full bg-primary/60 motion-safe:animate-ping"
        />
      )}
      <button
        type="button"
        aria-label={label}
        aria-pressed={recording}
        aria-busy={arming}
        aria-disabled={waiting ? true : undefined}
        disabled={disabled}
        onContextMenu={(event) => event.preventDefault()}
        {...(waiting ? waitingHandlers(waiting.nudge) : handlers)}
        className={cn(MIC_BUTTON, waiting && 'opacity-50', recording && 'scale-110 ring-4 ring-ring')}
      >
        <MicIcon arming={arming} shakeKey={waiting?.count || null} />
      </button>
    </span>
  )
}

export interface LockedMicButtonProps {
  label: string
  disabled: boolean
  /** Papá Noel está contestando: el toque explica que hay que esperar, como en `MicButton`. */
  waiting: { nudge: () => void; count: number } | null
  onPress: () => void
}

/**
 * El micrófono de un chat sin notas de voz (el público): el mismo botón, pero
 * un toque (o Intro/Espacio, que un `<button>` ya convierte en clic) no graba
 * ni pide permiso; llama a `onPress`.
 */
export function LockedMicButton({ label, disabled, waiting, onPress }: LockedMicButtonProps) {
  return (
    <button
      type="button"
      aria-label={label}
      aria-disabled={waiting ? true : undefined}
      disabled={disabled}
      onClick={waiting ? waiting.nudge : onPress}
      className={cn(MIC_BUTTON, waiting && 'opacity-50')}
    >
      <MicIcon arming={false} shakeKey={waiting?.count || null} />
    </button>
  )
}
